import React from "react";

const UI = {
  card: {
    background: "var(--br-surface, rgba(255,255,255,0.72))",
    border: "1px solid var(--br-border, rgba(148,163,184,0.18))",
    borderRadius: 18,
    padding: 14,
    height: "100%",
    minHeight: 170,
    boxShadow: "var(--br-shadow, 0 12px 30px rgba(15, 23, 42, 0.06))",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 10,
  },
  title: {
    fontFamily: "Manrope, sans-serif",
    fontWeight: 900,
    fontSize: 14,
    color: "var(--br-text, rgba(15,23,42,0.78))",
    letterSpacing: "-0.2px",
  },
  pill: {
    fontFamily: "Manrope, sans-serif",
    fontWeight: 900,
    fontSize: 11,
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid var(--br-border, rgba(148,163,184,0.22))",
    background: "var(--br-soft, rgba(255,255,255,0.75))",
    color: "var(--br-muted, rgba(15,23,42,0.55))",
    whiteSpace: "nowrap",
  },
  value: {
    fontFamily: "Manrope, sans-serif",
    fontWeight: 900,
    fontSize: 22,
    color: "var(--br-text, rgba(15,23,42,0.88))",
    letterSpacing: "-0.6px",
    lineHeight: "1.1",
  },
  sub: {
    fontFamily: "Manrope, sans-serif",
    fontWeight: 800,
    fontSize: 12,
    color: "var(--br-muted, rgba(15,23,42,0.42))",
  },
  footer: {
    fontFamily: "Manrope, sans-serif",
    fontWeight: 700,
    fontSize: 12,
    color: "var(--br-muted, rgba(15,23,42,0.45))",
  },
  body: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  empty: {
    height: "100%",
    width: "100%",
    borderRadius: 14,
    border: "1px dashed var(--br-border, rgba(148,163,184,0.35))",
    background: "var(--br-soft, rgba(255,255,255,0.35))",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    alignItems: "center",
    justifyContent: "center",
    padding: 12,
    textAlign: "center",
  },
  emptyTitle: {
    fontFamily: "Manrope, sans-serif",
    fontWeight: 900,
    fontSize: 13,
    color: "var(--br-muted, rgba(15,23,42,0.55))",
  },
  emptySub: {
    fontFamily: "Manrope, sans-serif",
    fontWeight: 700,
    fontSize: 12,
    color: "var(--br-muted, rgba(15,23,42,0.35))",
  },
};

const formatCompactNumber = (n, { decimals = 1 } = {}) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "";

  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  const round = (val, d) => {
    // avoid "161.0k" -> "161k"
    const fixed = val.toFixed(d);
    return fixed.replace(/\.0+$/, "").replace(/(\.\d*[1-9])0+$/, "$1");
  };

  if (abs >= 1e9) return `${sign}${round(abs / 1e9, decimals)}b`;
  if (abs >= 1e6) return `${sign}${round(abs / 1e6, decimals)}m`;
  if (abs >= 1e3) return `${sign}${round(abs / 1e3, decimals)}k`;
  return `${sign}${round(abs, 0)}`;
};

// If you pass value as "SGD 161,299.00" it will parse it.
// If you pass value as a number, even better.
const coerceNumeric = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const cleaned = value.replace(/[^\d.-]/g, ""); // remove currency, commas, spaces
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
  }
  return null;
};

export default function KPIChartCard({
  title,
  pill,
  value,
  subtitle,
  footer,
  children,
  isEmpty = false,
  emptyTitle = "No data yet",
  emptySub = "This will update once we have repayment history.",

  // ✅ new props
  isMobile = false,
  compactValueOnMobile = true,
  valuePrefixOnMobile = "", // e.g. "" to remove SGD; or "SGD " if you still want it
  compactDecimals = 1,
}) {
  const numericValue = coerceNumeric(value);

  const displayValue =
    isMobile && compactValueOnMobile && numericValue !== null
      ? `${valuePrefixOnMobile}${formatCompactNumber(numericValue, {
          decimals: compactDecimals,
        })}`
      : value;

  const cardStyle = {
    ...UI.card,
    padding: isMobile ? 10 : UI.card.padding,
    borderRadius: isMobile ? 16 : UI.card.borderRadius,
    minHeight: isMobile ? 150 : UI.card.minHeight,
    gap: isMobile ? 8 : UI.card.gap,
  };

  const titleStyle = {
    ...UI.title,
    fontSize: isMobile ? 12 : UI.title.fontSize,
  };

  const subStyle = {
    ...UI.sub,
    fontSize: isMobile ? 11 : UI.sub.fontSize,
  };

  const valueStyle = {
    ...UI.value,
    fontSize: isMobile ? 16 : UI.value.fontSize,
    letterSpacing: isMobile ? "-0.2px" : UI.value.letterSpacing,
  };

  const footerStyle = {
    ...UI.footer,
    fontSize: isMobile ? 11 : UI.footer.fontSize,
  };

  return (
    <div style={cardStyle}>
      <div style={UI.headerRow}>
        <div style={{ minWidth: 0 }}>
          <div style={titleStyle}>{title}</div>
          {subtitle ? <div style={subStyle}>{subtitle}</div> : null}
        </div>

        {/* ✅ hide pill on mobile */}
        {!isMobile && pill ? <div style={UI.pill}>{pill}</div> : null}
      </div>

      {displayValue ? <div style={valueStyle}>{displayValue}</div> : null}

      <div style={UI.body}>
        {isEmpty ? (
          <div style={UI.empty}>
            <div style={{ ...UI.emptyTitle, fontSize: isMobile ? 12 : 13 }}>
              {emptyTitle}
            </div>
            <div style={{ ...UI.emptySub, fontSize: isMobile ? 11 : 12 }}>
              {emptySub}
            </div>
          </div>
        ) : (
          children
        )}
      </div>

      {footer ? <div style={footerStyle}>{footer}</div> : null}
    </div>
  );
}
